import type { QuotaUsage } from '@simplemon/lib';
import { BarChart3, CheckCircle, Edit, Loader2, Plus, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotasApi } from '@/api/quotas';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Monitor {
  id: number;
  url: string;
  name: string;
  enabled: boolean;
  last_check: string | null;
  next_check: string | null;
  created_at: string;
  updated_at: string;
  rrule: string;
  total_checks: number;
  successful_checks: number;
  last_response_time: number | null;
}

export function MonitorsPage() {
  const navigate = useNavigate();
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [selectedMonitors, setSelectedMonitors] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [probeQuota, setProbeQuota] = useState<QuotaUsage | null>(null);
  const [quotaError, setQuotaError] = useState<string | null>(null);

  const fetchMonitors = useCallback(async () => {
    try {
      const response = await fetch('/api/monitors', {
        credentials: 'include',
      });
      const quota = await quotasApi
        .get('probe')
        .then((result) => {
          setQuotaError(null);
          return result;
        })
        .catch((error) => {
          console.error('Failed to load monitor quota:', error);
          setQuotaError('Unable to load quota usage');
          return null;
        });

      if (response.ok) {
        const data = await response.json();
        setMonitors(data);
      }

      if (quota) {
        setProbeQuota(quota);
      }
    } catch (error) {
      console.error('Failed to fetch monitors:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitors();
  }, [fetchMonitors]);

  const handleSelectMonitor = (monitorId: number) => {
    const newSelection = new Set(selectedMonitors);
    if (newSelection.has(monitorId)) {
      newSelection.delete(monitorId);
    } else {
      newSelection.add(monitorId);
    }
    setSelectedMonitors(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedMonitors.size === monitors.length) {
      setSelectedMonitors(new Set());
    } else {
      setSelectedMonitors(new Set(monitors.map((m) => m.id)));
    }
  };

  const handleBulkAction = async (action: 'enable' | 'disable') => {
    if (selectedMonitors.size === 0) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/monitors/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          monitorIds: Array.from(selectedMonitors),
          action,
        }),
      });

      if (response.ok) {
        await fetchMonitors();
        setSelectedMonitors(new Set());
      }
    } catch (error) {
      console.error(`Failed to ${action} monitors:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFrequency = (rrule: string) => {
    const match = rrule.match(/INTERVAL=(\d+)/);
    if (match) {
      const interval = Number.parseInt(match[1], 10);
      return `Every ${interval} minute${interval > 1 ? 's' : ''}`;
    }
    return rrule;
  };

  const getSuccessRate = (total: number, successful: number) => {
    if (total === 0) return '—';
    const rate = (successful / total) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const quotaReached = probeQuota ? probeQuota.used >= probeQuota.limit : false;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Monitoring Probes</h1>
              {probeQuota ? (
                <p className="mt-1 text-sm text-gray-500">
                  {probeQuota.used} out of {probeQuota.limit} in use
                </p>
              ) : quotaError ? (
                <p className="mt-1 text-sm text-gray-500">{quotaError}</p>
              ) : null}
            </div>
            <Button
              onClick={() => navigate('/dashboard/monitors/new')}
              className="flex items-center"
              disabled={quotaReached}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Probe
            </Button>
          </div>
          {quotaReached && probeQuota ? (
            <p className="mt-2 text-xs text-red-600">Quota reached. Remove a probe to add more.</p>
          ) : null}
        </div>

        {selectedMonitors.size > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {selectedMonitors.size} probe{selectedMonitors.size > 1 ? 's' : ''} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction('enable')}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enable
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulkAction('disable')}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Disable
            </Button>
          </div>
        )}

        {monitors.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500 mb-4">No monitoring probes configured yet.</p>
            <Button
              onClick={() => navigate('/dashboard/monitors/new')}
              className="inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Probe
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedMonitors.size === monitors.length && monitors.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Success Rate</TableHead>
                  <TableHead>Response Time</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {monitors.map((monitor) => (
                  <TableRow key={monitor.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedMonitors.has(monitor.id)}
                        onCheckedChange={() => handleSelectMonitor(monitor.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {monitor.enabled ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{monitor.name}</TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                      {monitor.url}
                    </TableCell>
                    <TableCell className="text-sm">{formatFrequency(monitor.rrule)}</TableCell>
                    <TableCell className="text-sm">
                      {getSuccessRate(monitor.total_checks, monitor.successful_checks)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {monitor.last_response_time !== null
                        ? `${monitor.last_response_time}ms`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/dashboard/monitors/${monitor.id}/results`)}
                          title="View Results"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/dashboard/monitors/edit?id=${monitor.id}`)}
                          title="Edit Monitor"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
